import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { type RecordOpening, useGetPermissionTargetsByIdsQuery } from "permissions/_api/permission-targets-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters, { PermissionOrderField } from "permissions/_api/queries/ordering/permission-order-params";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useGetPermissionsByGranteeQuery, useGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import { type RecordHolderRow, groupHolderGrants } from "permissions/_views/records/record-grants-model";
import { recordDomainOf } from "permissions/_views/records/record-permissions-path";
import { type NameState, nameStates } from "permissions/_views/targets/permission-target-state";
import Permission from "permissions/permission";
import { useMemo } from "react";

/**
 * Grants per page of a record's holders. Ordered by the holder's name, so one person's grants sit
 * together and only a person at a page's edge can be split across two.
 */
export const RECORD_GRANTS_PAGE_SIZE = 100;

/**
 * The filter a kind's grants on one record take: the kind's resource type and the record's id, which
 * the server matches against the id property the kind names (`GET /permissions?targetIds=`, #6174).
 */
export function recordFilter(kind: PermissionKind, recordId: Guid): Partial<PermissionFilterParameters> {
  if (!recordDomainOf(kind.resourceType)) throw new Error(`The record page draws no records of ${kind.resourceType}.`);
  return { resourceTypes: [kind.resourceType], targetIds: [recordId] };
}

/**
 * How many people hold a live grant on the record: the grantee view filtered to it, whose
 * `totalElements` is the number of people. Removed grants are left out, at the server's default.
 */
export function recordHoldersCountQuery(kind: PermissionKind, recordId: Guid): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Grantee,
    includePermissionPreviews: false,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 1 }),
    filterParameters: new PermissionFilterParameters(recordFilter(kind, recordId)),
  });
}

/** The record's grants, live and removed, read flat so each carries its parents for the Source cell. */
export function recordGrantsQuery(kind: PermissionKind, recordId: Guid, pageIndex: number): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex, pageSize: RECORD_GRANTS_PAGE_SIZE }),
    orderParameters: new PermissionOrderParameters({ orderBy: PermissionOrderField.GranteeName, orderDir: OrderDirection.Ascending }),
    filterParameters: new PermissionFilterParameters({
      showDisabled: true,
      ...recordFilter(kind, recordId),
    }),
  });
}

export type RecordGrants = Readonly<{
  /** One row per person on this page of grants. */
  rows: readonly RecordHolderRow[];
  /** Every grant on the record, live and removed, across all pages. */
  grantTotal: number;
  /** How many people hold a live grant on it; undefined until the count arrives. */
  holderCount?: number;
  isLoading: boolean;
  error?: string;
  retry: () => void;
  /** What the record is called, or why it cannot be named: missing, or not readable by the viewer. */
  target: NameState;
  /** When and by whom the record was opened, where its centre's read says so. */
  opened?: RecordOpening;
  retryTarget: () => void;
}>;

export default function useRecordGrants(kind: PermissionKind, recordId: Guid, pageIndex: number): RecordGrants {
  // Memoized on purpose: the queries are class instances that reach RTK Query's arguments.
  const countQuery = useMemo(() => recordHoldersCountQuery(kind, recordId), [kind, recordId]);
  const grantsQuery = useMemo(() => recordGrantsQuery(kind, recordId, pageIndex), [kind, recordId, pageIndex]);
  const countAnswer = useGetPermissionsByGranteeQuery({ query: countQuery }, { refetchOnFocus: true });
  const grantsAnswer = useGetPermissionsQuery({ query: grantsQuery }, { refetchOnFocus: true });

  const rows = useMemo(() => groupHolderGrants(Permission.fromJSONArray(grantsAnswer.data?.data ?? [])), [grantsAnswer.data]);

  const ids = useMemo(() => [recordId.value], [recordId]);
  const targetAnswer = useGetPermissionTargetsByIdsQuery(Guid.isValid(recordId.value) ? { resourceType: kind.resourceType, ids } : skipToken);
  const target = nameStates(ids, { data: targetAnswer.data, isError: targetAnswer.isError }).get(recordId.value) ?? { state: "loading" };
  const opened = targetAnswer.data?.found.find((record) => record.id.toLowerCase() === recordId.value.toLowerCase())?.opened;

  const failure = grantsAnswer.error ?? countAnswer.error;
  return {
    rows,
    grantTotal: grantsAnswer.data?.headers.totalElements ?? 0,
    holderCount: countAnswer.data?.headers.totalElements,
    isLoading: grantsAnswer.isLoading,
    error: failure ? (getErrorMessageFromUnknown(failure) ?? "The grants could not be loaded.") : undefined,
    retry: () => {
      void grantsAnswer.refetch();
      void countAnswer.refetch();
    },
    target,
    opened,
    retryTarget: () => void targetAnswer.refetch(),
  };
}
